import { MembershipTier } from './types.ts';

export type TierFeature =
  | 'googleMaps'
  | 'advancedDataQuality'
  | 'premiumReports'
  | 'collaboraEditor'
  | 'archiveRecords' // Onderzoeker+: akte openen en importeren; zoeken is voor iedereen (#1730)
  | 'archiveSuggestions'
  | 'sourceVerification' // Pro+: bronnen na import verifiëren bij Open Archieven
  // Item 5/6 — export-specifieke tier-features
  | 'multiPageExport' // Pro+: 2/4/6 vellen + 'true-size'
  | 'customBranding' // Business: eigen logo/footer ipv Pecktree-branding
  | 'highResExport'; // Pro+: 200+ DPI PNG (Free is gecapt op 150 DPI)

export type TierLimit =
  | 'maxPeople'
  | 'maxProjects'
  | 'maxViewersPerProject'
  | 'maxEditorsPerProject'
  | 'maxGenerations'
  | 'storageBytes'
  | 'mediaStorageBytes';

export interface TierLimits {
  maxPeople: number; // -1 = unlimited
  maxProjects: number; // -1 = unlimited
  maxViewersPerProject: number; // meekijkers (alleen lezen); -1 = unlimited
  maxEditorsPerProject: number; // bewerkers; 0 = none, -1 = unlimited
  maxGenerations: number; // -1 = unlimited
  storageBytes: number; // in bytes
  mediaStorageBytes: number; // in bytes
  features: Record<TierFeature, boolean>;
}

const GB = 1024 * 1024 * 1024;
const MB = 1024 * 1024;

// #1730 — intern free/pro/business; zichtbaar Gratis / Onderzoeker / Professional
// (Bureau = organisatielicentie, effectief business). Limieten moeten gelijk
// blijven met TIER_LIMITS in shared/auth-helpers.js. Geen boomdiepte-limiet meer:
// maxGenerations is overal -1 (useTreeState houdt de BFS-veiligheid van 15).
export const TIER_CONFIG: Record<MembershipTier, TierLimits> = {
  free: {
    maxPeople: 250,
    maxProjects: 1,
    maxViewersPerProject: 3,
    maxEditorsPerProject: 0,
    maxGenerations: -1,
    storageBytes: 1 * GB,
    mediaStorageBytes: 50 * MB,
    features: {
      googleMaps: false,
      advancedDataQuality: false,
      premiumReports: false,
      collaboraEditor: false,
      archiveRecords: false,
      archiveSuggestions: false,
      sourceVerification: false,
      multiPageExport: false,
      customBranding: false,
      highResExport: false,
    },
  },
  pro: {
    maxPeople: -1,
    maxProjects: -1,
    maxViewersPerProject: -1,
    maxEditorsPerProject: 5,
    maxGenerations: -1,
    storageBytes: 10 * GB,
    mediaStorageBytes: 10 * GB,
    features: {
      googleMaps: true,
      advancedDataQuality: true,
      premiumReports: true,
      collaboraEditor: true,
      archiveRecords: true,
      archiveSuggestions: false,
      sourceVerification: true,
      multiPageExport: true,
      customBranding: false,
      highResExport: true,
    },
  },
  business: {
    maxPeople: -1,
    maxProjects: -1,
    maxViewersPerProject: -1,
    maxEditorsPerProject: -1,
    maxGenerations: -1,
    storageBytes: 100 * GB,
    mediaStorageBytes: 100 * GB,
    features: {
      googleMaps: true,
      advancedDataQuality: true,
      premiumReports: true,
      collaboraEditor: true,
      archiveRecords: true,
      archiveSuggestions: true,
      sourceVerification: true,
      multiPageExport: true,
      customBranding: true,
      highResExport: true,
    },
  },
};

export const TIER_LEVEL: Record<MembershipTier, number> = {
  free: 0,
  pro: 1,
  business: 2,
};

/**
 * De tier die telt: een toegewezen Business-licentie zet `effectiveTier` (server,
 * /auth/me) boven de accounttier. Buiten React (export-handlers) is dit de
 * tegenhanger van `useTierAccess().tier` (#1441).
 */
export function effectieveTier(
  user: { tier?: string | null; effectiveTier?: string | null } | null | undefined
): MembershipTier {
  const t = (user?.effectiveTier || user?.tier || 'free') as MembershipTier;
  return t in TIER_CONFIG ? t : 'free';
}

/**
 * #1730 — i18n-sleutel voor de naam die gebruikers zien: Gratis / Onderzoeker /
 * Professional, en Bureau als de business-tier van een organisatielicentie komt.
 */
export function tierNameKey(tier: MembershipTier, bureau = false): string {
  if (tier === 'business' && bureau) return 'tierName_bureau';
  return `tierName_${tier in TIER_CONFIG ? tier : 'free'}`;
}

export function tierHasFeature(tier: MembershipTier, feature: TierFeature): boolean {
  return TIER_CONFIG[tier]?.features[feature] ?? false;
}

export function tierGetLimit(tier: MembershipTier, limit: TierLimit): number {
  return TIER_CONFIG[tier]?.[limit] ?? 0;
}

export function isUnlimited(tier: MembershipTier, limit: TierLimit): boolean {
  return tierGetLimit(tier, limit) === -1;
}

export function minimumTierForFeature(feature: TierFeature): MembershipTier {
  if (TIER_CONFIG.free.features[feature]) return 'free';
  if (TIER_CONFIG.pro.features[feature]) return 'pro';
  return 'business';
}

export const FREE_DATA_QUALITY_CATEGORIES = ['dates', 'age'] as const;
